import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

// Icons
import Download from '@spectrum-icons/workflow/Download';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import PlayCircle from '@spectrum-icons/workflow/PlayCircle';

// Lodash
import _ from 'lodash';

// Util
import { normalizeUrl } from '../../util/util';

// CSS
import '../../styles/ContentCard.scss';

const ContentCard = ({
  thumbnailImageUrl,
  playIcon,
  ribbonText,
  header,
  description,
  descriptionIsAddress,
  linkUrl,
  linkText,
  linkIcon,
  wrapCardInLink,
  cardWidth,
}) => {
  const [thumbnailImageUrlResolved, setThumbnailImageUrlResolved] = useState(null);

  useEffect(() => {
    if (thumbnailImageUrl.includes('youtube.com') || thumbnailImageUrl.includes('youtu.be')) {
      // Try to get the video ID.
      const videoId = thumbnailImageUrl.match(/.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#&?]*).*/);
      if (_.size(videoId) > 1) {
        setThumbnailImageUrlResolved(`https://img.youtube.com/vi/${videoId[1]}/maxresdefault.jpg`);
      }
    } else if (thumbnailImageUrl.includes('vimeo.com')) {
      // Fetch Vimeo metadata to get the thumbnail.
      fetch(`https://vimeo.com/api/oembed.json?url=${thumbnailImageUrl}`)
        .then((response) => response.json())
        .then((data) => {
          if (_.has(data, 'thumbnail_url')) {
            setThumbnailImageUrlResolved(data.thumbnail_url);
          }
        })
        .catch(() => {
          window.console.error('Unable to fetch Vimeo thumbnail.');
        });
    }
  }, [thumbnailImageUrl]);

  // Helper functions
  const cardWidthClass = () => {
    switch (cardWidth) {
      case '1':
        return 'card-width-1';
      case '3':
        return 'card-width-3';
      default:
        return 'card-width-2';
    }
  };

  const renderPlayIcon = () => {
    if (playIcon) {
      return (
        <div className="playIcon">
          <PlayCircle size="XL" />
        </div>
      );
    }
    return null;
  };

  const renderDescription = () => {
    if (description) {
      if (descriptionIsAddress) {
        return description.map((addressLine) => (<p key={addressLine} className="addressLine">{addressLine}</p>));
      }
      return <p>{description}</p>;
    }
    return null;
  };

  const renderThumbnail = () => {
    if (thumbnailImageUrlResolved) {
      return (
        <div className="thumbnail" style={{ background: `url(${thumbnailImageUrlResolved})` }}>
          {renderPlayIcon()}
        </div>
      );
    }
    return null;
  };

  const renderRibbon = () => {
    if (ribbonText) {
      return (
        <div className="ribbon">
          {ribbonText}
        </div>
      );
    }
    return null;
  };

  const renderLink = () => {
    if (linkText) {
      let icon = null;
      switch (linkIcon) {
        case 'link':
          icon = <LinkOut size="S" />;
          break;
        case 'download':
          icon = <Download size="S" />;
          break;
        default:
          break;
      }
      return (
        <div className="link">
          {icon}
          <a href={normalizeUrl(linkUrl)} target="_blank" rel="noreferrer">
            {linkText}
          </a>
        </div>
      );
    }
    return null;
  };

  const renderCard = () => {
    const contentSectionClass = `content-section ${ribbonText && !thumbnailImageUrl && cardWidth !== '3' ? 'hasRibbon' : ''}`;
    if (wrapCardInLink) {
      return (
        <div
          className={`cmp-content-card ${cardWidthClass()} as-button`}
          role="button"
          tabIndex={0}
          onClick={() => window.open(normalizeUrl(linkUrl), '_blank')}
          onKeyPress={() => window.open(normalizeUrl(linkUrl), '_blank')}
        >
          {renderRibbon()}
          {renderThumbnail()}
          <div className={contentSectionClass}>
            <h1>{header}</h1>
            {renderDescription()}
            {renderLink()}
          </div>
        </div>
      );
    }
    return (
      <div className={`cmp-content-card ${cardWidthClass()}`}>
        {renderRibbon()}
        {renderThumbnail()}
        <div className={contentSectionClass}>
          <h1>{header}</h1>
          {renderDescription()}
          {renderLink()}
        </div>
      </div>
    );
  };

  return renderCard();
};

ContentCard.propTypes = {
  thumbnailImageUrl: PropTypes.string,
  playIcon: PropTypes.bool,
  ribbonText: PropTypes.string,
  header: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  descriptionIsAddress: PropTypes.bool,
  linkUrl: PropTypes.string,
  linkText: PropTypes.string,
  linkIcon: PropTypes.string,
  wrapCardInLink: PropTypes.bool,
  cardWidth: PropTypes.string,
};

ContentCard.defaultProps = {
  thumbnailImageUrl: '',
  playIcon: false,
  ribbonText: '',
  header: '',
  description: '',
  descriptionIsAddress: false,
  linkUrl: '',
  linkText: '',
  linkIcon: '',
  wrapCardInLink: false,
  cardWidth: '1',
};

export default ContentCard;
